// Exportes a Excel. Funciones de armado puras (testeables) + descarga con SheetJS (se carga
// recién al exportar). La descarga completa existe en cualquier estado de la licencia: los
// datos son del negocio.
import type { LocalDb, SaleRow } from '@pos/local-db';
import { supabase } from '../../lib/supabase.ts';
import { FULL_EXPORT_TABLES, type Sheet } from './sheets.ts';

export { FULL_EXPORT_TABLES, salesSheets, type Sheet } from './sheets.ts';

/** Descarga un libro de Excel con las hojas dadas. */
export async function downloadWorkbook(fileName: string, sheets: Sheet[]): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  for (const s of sheets) {
    const ws = XLSX.utils.json_to_sheet(s.rows.length ? s.rows : [{ '(sin datos)': '' }]);
    // Nombres de hoja: máximo 31 caracteres y sin / \ ? * [ ].
    XLSX.utils.book_append_sheet(wb, ws, s.name.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31));
  }
  XLSX.writeFile(wb, fileName);
}

/** Trae todas las filas de una consulta, de a 1000 (límite del servidor por pedido). */
async function fetchAll(table: string, businessId: string, select = '*', order = 'id'): Promise<Array<Record<string, unknown>>> {
  const rows: Array<Record<string, unknown>> = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase.from(table).select(select).eq('business_id', businessId).order(order).range(from, from + 999);
    if (error) throw new Error(`${table}: ${error.message}`);
    rows.push(...((data ?? []) as unknown as Array<Record<string, unknown>>));
    if (!data || data.length < 1000) return rows;
  }
}

/** Ventas de un período desde el servidor (con ítems y pagos). */
export async function fetchSalesForExport(
  businessId: string,
  from: string,
  to: string,
  unitOf: (variantId: string) => SaleRow['items'][number]['unit_type'],
): Promise<SaleRow[]> {
  const out: SaleRow[] = [];
  for (let offset = 0; ; offset += 500) {
    const { data, error } = await supabase
      .from('sales')
      .select('*, sale_items(*), sale_payments(*)')
      .eq('business_id', businessId)
      .gte('occurred_at', from)
      .lt('occurred_at', to)
      .order('occurred_at')
      .range(offset, offset + 499);
    if (error) throw new Error(error.message);
    for (const r of (data ?? []) as Array<Record<string, unknown>>) {
      const items = (r.sale_items as Array<Record<string, unknown>>).sort((a, b) => Number(a.line_no) - Number(b.line_no));
      out.push({
        ...(r as unknown as SaleRow),
        items: items.map((i) => ({ ...(i as unknown as SaleRow['items'][number]), unit_type: unitOf(String(i.variant_id)), movement_id: '' })),
        payments: r.sale_payments as SaleRow['payments'],
      });
    }
    if (!data || data.length < 500) return out;
  }
}

/** Valores para Excel: objetos (jsonb) como texto. */
function flatten(row: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v !== null && typeof v === 'object' ? JSON.stringify(v) : v]));
}

/**
 * Descarga completa: con conexión, todo lo del servidor; sin conexión, lo que hay en este
 * dispositivo (avisado en la primera hoja). onProgress informa la tabla que se está bajando.
 */
export async function fullExport(businessId: string, businessName: string, localDb: LocalDb, onProgress: (text: string) => void): Promise<void> {
  const sheets: Sheet[] = [];
  const online = navigator.onLine;
  for (const t of FULL_EXPORT_TABLES) {
    onProgress(t.sheet);
    let rows: Array<Record<string, unknown>>;
    if (online) {
      rows = t.table === 'businesses' ? await fetchBusiness(businessId) : await fetchAll(t.table, businessId, '*', t.order ?? 'id');
    } else {
      const local = (localDb.tables.find((x) => x.name === t.table) ?? null) as { toArray: () => Promise<Array<Record<string, unknown>>> } | null;
      rows = local ? (await local.toArray()).filter((r) => r.business_id === businessId || r.id === businessId) : [];
    }
    sheets.push({ name: t.sheet, rows: rows.map(flatten) });
  }
  const now = new Date();
  sheets.unshift({
    name: 'Leeme',
    rows: [
      { Dato: 'Negocio', Valor: businessName },
      { Dato: 'Descargado', Valor: now.toLocaleString('es-AR') },
      {
        Dato: 'Origen',
        Valor: online ? 'Servidor (todos los dispositivos)' : 'SOLO este dispositivo (sin conexión): puede faltar información de otros dispositivos',
      },
      { Dato: 'Importes', Valor: 'En centavos (dividir por 100). Cantidades en milésimas (dividir por 1000).' },
    ],
  });
  await downloadWorkbook(`datos-${businessName.replace(/[^\w-]+/g, '-').toLowerCase()}-${now.toISOString().slice(0, 10)}.xlsx`, sheets);
}

async function fetchBusiness(businessId: string) {
  const { data, error } = await supabase.from('businesses').select('*').eq('id', businessId);
  if (error) throw new Error(error.message);
  return (data ?? []) as Array<Record<string, unknown>>;
}
